import { cn } from '../../lib/cn.ts';
import { Card } from '../card.tsx';

export interface ChartSeries {
  id: string;
  label: string;
  /** A token color string, e.g. 'var(--color-charcoal)'. */
  color: string;
  data: number[];
  dashed?: boolean;
  filled?: boolean;
}

export interface LineChartCardProps {
  title: string;
  series: ChartSeries[];
  xLabels?: string[];
  className?: string;
  'data-testid'?: string;
}

const VB_W = 600;
const VB_H = 220;
const PAD = { top: 16, right: 8, bottom: 24, left: 8 };

/**
 * LineChartCard — a quiet, printed-feeling multi-series area/line chart drawn by
 * hand in SVG (no charting dependency). The legend conveys each series by text
 * label, never by colour alone.
 */
export function LineChartCard({
  title,
  series,
  xLabels,
  className,
  'data-testid': testId,
}: LineChartCardProps) {
  const allValues = series.flatMap((s) => s.data);
  const max = allValues.length ? Math.max(...allValues) : 1;
  const min = Math.min(0, ...allValues);
  const span = max - min || 1;
  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = VB_H - PAD.top - PAD.bottom;

  const toPoints = (data: number[]) => {
    const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
    return data.map((v, i) => {
      const x = PAD.left + i * stepX;
      const y = PAD.top + innerH - ((v - min) / span) * innerH;
      return [x, y] as const;
    });
  };
  const linePath = (pts: ReadonlyArray<readonly [number, number]>) =>
    pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = (pts: ReadonlyArray<readonly [number, number]>) => {
    if (pts.length < 2) return '';
    const last = pts[pts.length - 1];
    const first = pts[0];
    if (!last || !first) return '';
    return `${linePath(pts)} L${last[0].toFixed(1)} ${(PAD.top + innerH).toFixed(1)} L${first[0].toFixed(1)} ${(PAD.top + innerH).toFixed(1)} Z`;
  };
  const grid = [0, 0.5, 1].map((t) => PAD.top + innerH * t);

  return (
    <Card data-testid={testId} className="p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="font-[var(--font-display)] text-lg font-medium tracking-tight text-[var(--color-charcoal)]">
          {title}
        </h3>
        <ul className="flex items-center gap-4">
          {series.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-1.5 font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]"
            >
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </li>
          ))}
        </ul>
      </div>
      <svg
        role="img"
        aria-label={`${title}. ${series.map((s) => `${s.label}: ${s.data.join(', ')}`).join('. ')}`}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        className={cn('h-56 w-full', className)}
      >
        {grid.map((y) => (
          <line
            key={y}
            x1={PAD.left}
            x2={VB_W - PAD.right}
            y1={y}
            y2={y}
            className="stroke-[var(--color-stone-200)]"
            strokeWidth={1}
          />
        ))}
        {series.map((s) => {
          const pts = toPoints(s.data);
          return (
            <g key={s.id}>
              {s.filled && (
                <path d={areaPath(pts)} style={{ fill: s.color }} className="opacity-[0.06]" />
              )}
              <path
                d={linePath(pts)}
                fill="none"
                style={{ stroke: s.color }}
                strokeWidth={2}
                strokeDasharray={s.dashed ? '4 4' : undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          );
        })}
      </svg>
      {xLabels && (
        <div className="mt-1 flex justify-between font-[var(--font-mono)] text-[10px] text-[var(--color-stone-mid)]">
          {xLabels.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: x-axis labels are fixed positional ticks
            <span key={`${l}-${i}`}>{l}</span>
          ))}
        </div>
      )}
    </Card>
  );
}
